import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Key } from "ts-key-enum";
import { t } from "ttag";

import { RichText } from "../../../components/RichText";
import { IOptionValues } from "../../../models/catalogue";
import { useAppSelector } from "../../../store/reducers";
import { TabFeatureAttributeBlockAttributeTabButton } from "../TabFeatureAttributeBlockAttributeTabButton";
import { TabFeatureAttributeBlockAttributeTabContent } from "../TabFeatureAttributeBlockAttributeTabContent";
import { ITabFeatureAttributeBlock } from "../models";
import { getOptionValueByIdx } from "../utils";

import styles from "./index.module.scss";

export const TabFeatureAttributeBlock: FC<ITabFeatureAttributeBlock> = (
    props,
) => {
    const optionValues = useAppSelector(
        (state) => state.configurator.ui.optionValues,
    );
    const [selectedAttrValue, setSelectedAttrValue] = useState<string | null>(
        null,
    );

    // Create a set of valid attribute values from props.tabs
    const validValues = props.tabs.map((tab) => tab.attribute_value);

    // Compute selected attribute value
    const getComputedSelectedAttrValue = (): string | null => {
        if (selectedAttrValue && validValues.includes(selectedAttrValue)) {
            return selectedAttrValue;
        }
        if (props.tabs.length > 0) {
            return props.tabs[0].attribute_value;
        }
        return null;
    };
    const computedSelectedAttrValue = getComputedSelectedAttrValue();

    // Get selected attribute from redux
    const getSelectedAttributeFromRedux = (optionValues: IOptionValues) => {
        const selectedReduxValues = optionValues[props.attribute];
        if (!selectedReduxValues) {
            return;
        }
        return getOptionValueByIdx(selectedReduxValues, 0);
    };

    // Sync with redux on mount and when optionValues change
    useEffect(() => {
        const selectedReduxValue = getSelectedAttributeFromRedux(optionValues);
        setSelectedAttrValue(selectedReduxValue || null);
    }, [optionValues]);

    const attributeTabRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const handleAttributeTabKeyDown = useCallback(
        (e: React.KeyboardEvent, index: number) => {
            const count = props.tabs?.length ?? 0;
            if (count === 0) return;

            if (e.key === Key.ArrowLeft) {
                e.preventDefault();
                const prevIndex = index > 0 ? index - 1 : count - 1;
                const prev = attributeTabRefs.current[prevIndex];
                prev?.focus();
                prev?.click();
            }
            if (e.key === Key.ArrowRight) {
                e.preventDefault();
                const nextIndex = index < count - 1 ? index + 1 : 0;
                const next = attributeTabRefs.current[nextIndex];
                next?.focus();
                next?.click();
            }
            if (e.key === Key.Home && attributeTabRefs.current[0]) {
                e.preventDefault();
                const first = attributeTabRefs.current[0];
                first.focus();
                first.click();
            }
            if (e.key === Key.End && attributeTabRefs.current[count - 1]) {
                e.preventDefault();
                const last = attributeTabRefs.current[count - 1];
                last?.focus();
                last?.click();
            }
        },
        [props.tabs?.length],
    );

    // Check if tabs are defined
    if (!props.tabs) {
        return null;
    }

    return (
        <div className={`u-flex-container ${styles.root}`}>
            <header className={styles.header}>
                <RichText html={props.header_content} />
            </header>
            <div
                className={styles.tablist}
                role="tablist"
                aria-label={t`Tab Feature Attribute Block - Tab List`}
            >
                {props.tabs.map((attr, i: number) => (
                    <TabFeatureAttributeBlockAttributeTabButton
                        key={attr.attribute_value}
                        ref={(el) => {
                            attributeTabRefs.current[i] = el;
                        }}
                        idx={i}
                        tab={attr}
                        onClick={() => {
                            setSelectedAttrValue(attr.attribute_value);
                        }}
                        isSelected={
                            attr.attribute_value === computedSelectedAttrValue
                        }
                        onKeyDown={(e) => handleAttributeTabKeyDown(e, i)}
                    />
                ))}
                <div className={styles.tablistBar}></div>
            </div>
            <div className={styles.content}>
                {props.tabs.map((attr, i: number) => (
                    <TabFeatureAttributeBlockAttributeTabContent
                        key={attr.attribute_value}
                        attribute={props.attribute}
                        idx={i}
                        tab={attr}
                        isSelected={
                            attr.attribute_value === computedSelectedAttrValue
                        }
                    />
                ))}
            </div>
        </div>
    );
};
